import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { FiPlus } from 'react-icons/fi';
import MediaUploadSection from './MediaUploadSection.jsx';
import { SERVICE_OPTIONS } from '../utils/serviceOptions';

const inputClass =
    'mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold py-2 px-2';

const normalizePhone = (value) => {
    if (value == null) return '';
    const cleaned = String(value).replace(/\s/g, '').trim();
    if (!cleaned) return '';
    if (cleaned.startsWith('+')) return cleaned;
    if (/^\d{8}$/.test(cleaned)) return '+65' + cleaned;
    return cleaned;
};

const formatAgeForInput = (age) => {
    if (!age) return '';
    const d = new Date(age);
    return Number.isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
};

const normalizeService = (serviceArr) => {
    if (!Array.isArray(serviceArr) || serviceArr.length === 0) return ['Massage'];
    const filtered = serviceArr.filter((s) => typeof s === 'string' && SERVICE_OPTIONS.includes(s));
    return filtered.length ? filtered : ['Massage'];
};

const normalizeContactEntries = (arr) => {
    if (!Array.isArray(arr) || arr.length === 0) {
        return [{ contactNumber: '', isActive: true }];
    }
    return arr.map((entry) => {
        if (typeof entry === 'string') {
            return { contactNumber: entry, isActive: true };
        }
        return {
            contactNumber: entry.contactNumber || '',
            isActive: entry.isActive !== false,
        };
    });
};

const normalizeWhatsAppEntries = (arr) => {
    if (!Array.isArray(arr) || arr.length === 0) {
        return [{ whatsAppNumber: '', isActive: true }];
    }
    return arr.map((entry) => {
        if (typeof entry === 'string') {
            return { whatsAppNumber: entry, isActive: true };
        }
        return {
            whatsAppNumber: entry.whatsAppNumber || entry.whatsappNumber || '',
            isActive: entry.isActive !== false,
        };
    });
};

const EditNonMemberForm = ({ nonMember, onSuccess, onCancel }) => {
    const [formData, setFormData] = useState({
        name: nonMember?.name || '',
        nationality: nonMember?.nationality || '',
        age: formatAgeForInput(nonMember?.age),
        webID: nonMember?.webID || '',
        region: nonMember?.region || '',
        area: nonMember?.area || '',
        contactNumbers: normalizeContactEntries(nonMember?.contactNumbers),
        whatsappNumbers: normalizeWhatsAppEntries(nonMember?.whatsappNumbers),
        remarks: nonMember?.remarks || '',
        website: nonMember?.website || '',
        priceHr: nonMember?.priceHr != null && nonMember.priceHr !== '' ? String(nonMember.priceHr) : '',
        service: normalizeService(nonMember?.service),
        extraService: nonMember?.extraService || '',
        websiteRemarks: nonMember?.websiteRemarks || '',
        openHour: nonMember?.openHour || '',
        closeHour: nonMember?.closeHour || '',
        newSetupDone: Boolean(nonMember?.newSetupDone),
        wechatQR: nonMember?.wechatQR || [],
        standardPhotos: nonMember?.standardPhotos || [],
        icecreamPhotos: nonMember?.icecreamPhotos || [],
        standardVideos: nonMember?.standardVideos || [],
        icecreamVideos: nonMember?.icecreamVideos || [],
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const priceHrFieldLabel = useMemo(() => 'Price / hr (optional)', []);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const toggleServiceOption = (opt) => {
        setFormData((prev) => {
            const current = prev.service || [];
            const next = current.includes(opt) ? current.filter((s) => s !== opt) : [...current, opt];
            return { ...prev, service: next.length ? next : ['Massage'] };
        });
    };

    const handleContactNumberChange = (index, value) => {
        const newContactNumbers = [...formData.contactNumbers];
        newContactNumbers[index] = { ...newContactNumbers[index], contactNumber: value };
        setFormData((prev) => ({ ...prev, contactNumbers: newContactNumbers }));
    };

    const handleContactNumberBlur = (index) => {
        const v = formData.contactNumbers[index]?.contactNumber;
        const normalized = normalizePhone(v);
        if (normalized !== v) {
            const newContactNumbers = [...formData.contactNumbers];
            newContactNumbers[index] = { ...newContactNumbers[index], contactNumber: normalized };
            setFormData((prev) => ({ ...prev, contactNumbers: newContactNumbers }));
        }
    };

    const addContactNumber = () => {
        setFormData((prev) => ({
            ...prev,
            contactNumbers: [...prev.contactNumbers, { contactNumber: '', isActive: true }],
        }));
    };

    const removeContactNumber = (index) => {
        setFormData((prev) => ({
            ...prev,
            contactNumbers: prev.contactNumbers.filter((_, i) => i !== index),
        }));
    };

    const handleWhatsAppNumberChange = (index, value) => {
        const newWhatsAppNumbers = [...formData.whatsappNumbers];
        newWhatsAppNumbers[index] = { ...newWhatsAppNumbers[index], whatsAppNumber: value };
        setFormData((prev) => ({ ...prev, whatsappNumbers: newWhatsAppNumbers }));
    };

    const handleWhatsAppNumberBlur = (index) => {
        const v = formData.whatsappNumbers[index]?.whatsAppNumber;
        const normalized = normalizePhone(v);
        if (normalized !== v) {
            const newWhatsAppNumbers = [...formData.whatsappNumbers];
            newWhatsAppNumbers[index] = { ...newWhatsAppNumbers[index], whatsAppNumber: normalized };
            setFormData((prev) => ({ ...prev, whatsappNumbers: newWhatsAppNumbers }));
        }
    };

    const addWhatsAppNumber = () => {
        setFormData((prev) => ({
            ...prev,
            whatsappNumbers: [...prev.whatsappNumbers, { whatsAppNumber: '', isActive: true }],
        }));
    };

    const removeWhatsAppNumber = (index) => {
        setFormData((prev) => ({
            ...prev,
            whatsappNumbers: prev.whatsappNumbers.filter((_, i) => i !== index),
        }));
    };

    const buildPayload = () => {
        const rawService = (formData.service || []).filter((s) => SERVICE_OPTIONS.includes(s));
        const priceHrParsed =
            formData.priceHr === '' || formData.priceHr == null ? undefined : Number(formData.priceHr);
        return {
            name: formData.name.trim(),
            nationality: formData.nationality.trim(),
            age: formData.age ? new Date(formData.age).toISOString() : undefined,
            webID: formData.webID.trim(),
            region: formData.region.trim() || undefined,
            area: formData.area.trim() || undefined,
            contactNumbers: formData.contactNumbers
                .filter((entry) => entry.contactNumber && entry.contactNumber.trim() !== '')
                .map((entry) => ({
                    contactNumber: normalizePhone(entry.contactNumber) || entry.contactNumber.trim(),
                    isActive: entry.isActive !== false,
                })),
            whatsappNumbers: formData.whatsappNumbers
                .filter((entry) => entry.whatsAppNumber && entry.whatsAppNumber.trim() !== '')
                .map((entry) => ({
                    whatsAppNumber: normalizePhone(entry.whatsAppNumber) || entry.whatsAppNumber.trim(),
                    isActive: entry.isActive !== false,
                })),
            remarks: formData.remarks.trim(),
            website: formData.website.trim(),
            priceHr: Number.isFinite(priceHrParsed) && priceHrParsed >= 0 ? priceHrParsed : undefined,
            service: rawService.length ? rawService : ['Massage'],
            extraService: formData.extraService.trim() || undefined,
            websiteRemarks: formData.websiteRemarks.trim() || undefined,
            openHour: formData.openHour || undefined,
            closeHour: formData.closeHour || undefined,
            newSetupDone: formData.newSetupDone,
        };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            const token = localStorage.getItem('token');
            const response = await axios.put(
                `${process.env.REACT_APP_API_URL}/api/nonmembers/${nonMember._id}`,
                buildPayload(),
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true',
                    },
                }
            );
            if (response.data.success) {
                onSuccess?.(response.data.data);
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to update non-member');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-start justify-center pt-8 pb-12">
            <div className="relative max-w-5xl w-full mx-4 p-6 border shadow-lg rounded-lg bg-white">
                <div className="flex justify-between items-center mb-6 gap-4">
                    <div className="flex items-center gap-3 shrink-0">
                        <h2 className="text-xl font-bold text-gray-900">Edit Non-Member</h2>
                        <label className="inline-flex items-center">
                            <input
                                type="checkbox"
                                name="newSetupDone"
                                checked={formData.newSetupDone}
                                onChange={handleChange}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="ml-2 text-sm text-gray-700">New setup done</span>
                        </label>
                    </div>
                    <button
                        type="button"
                        onClick={onCancel}
                        className="text-gray-500 hover:text-gray-700 text-2xl leading-none p-1 shrink-0"
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>

                {error && (
                    <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">{error}</div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Name</label>
                            <input name="name" value={formData.name} onChange={handleChange} className={inputClass} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Web ID</label>
                            <input name="webID" value={formData.webID} onChange={handleChange} className={inputClass} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Nationality</label>
                            <input name="nationality" value={formData.nationality} onChange={handleChange} className={inputClass} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Age (DOB)</label>
                            <input type="date" name="age" value={formData.age} onChange={handleChange} className={inputClass} />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Region</label>
                            <input name="region" value={formData.region} onChange={handleChange} className={inputClass} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Area</label>
                            <input name="area" value={formData.area} onChange={handleChange} className={inputClass} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Opening hour</label>
                            <input type="time" name="openHour" value={formData.openHour} onChange={handleChange} className={inputClass} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Closing hour</label>
                            <input type="time" name="closeHour" value={formData.closeHour} onChange={handleChange} className={inputClass} />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{priceHrFieldLabel}</label>
                            <input type="number" min="0" step="0.01" name="priceHr" value={formData.priceHr} onChange={handleChange} className={inputClass} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Website</label>
                            <input name="website" value={formData.website} onChange={handleChange} className={inputClass} />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="block text-sm font-medium text-gray-700">Contact Numbers</label>
                                <button
                                    type="button"
                                    onClick={addContactNumber}
                                    className="inline-flex items-center gap-1 px-2 py-1 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                                    title="Add contact number"
                                >
                                    <FiPlus className="w-4 h-4" />
                                    Add
                                </button>
                            </div>
                            {formData.contactNumbers.map((entry, index) => (
                                <div key={index} className="flex gap-2 mb-2">
                                    <input
                                        type="text"
                                        value={entry.contactNumber || ''}
                                        onChange={(e) => handleContactNumberChange(index, e.target.value)}
                                        onBlur={() => handleContactNumberBlur(index)}
                                        placeholder="e.g. 12345678 → +65"
                                        className="flex-1 rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold py-2 px-2"
                                    />
                                    {index > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => removeContactNumber(index)}
                                            className="px-3 py-2 bg-red-100 text-red-600 rounded-md hover:bg-red-200 text-sm"
                                        >
                                            Remove
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="block text-sm font-medium text-gray-700">WhatsApp Numbers</label>
                                <button
                                    type="button"
                                    onClick={addWhatsAppNumber}
                                    className="inline-flex items-center gap-1 px-2 py-1 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                                    title="Add WhatsApp number"
                                >
                                    <FiPlus className="w-4 h-4" />
                                    Add
                                </button>
                            </div>
                            {formData.whatsappNumbers.map((entry, index) => (
                                <div key={index} className="flex gap-2 mb-2">
                                    <input
                                        type="text"
                                        value={entry.whatsAppNumber || ''}
                                        onChange={(e) => handleWhatsAppNumberChange(index, e.target.value)}
                                        onBlur={() => handleWhatsAppNumberBlur(index)}
                                        placeholder="e.g. 88881234 → +65"
                                        className="flex-1 rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold py-2 px-2"
                                    />
                                    {index > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => removeWhatsAppNumber(index)}
                                            className="px-3 py-2 bg-red-100 text-red-600 rounded-md hover:bg-red-200 text-sm"
                                        >
                                            Remove
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Service</label>
                        <div className="flex flex-wrap gap-x-4 gap-y-2 p-3 rounded-md border border-gray-200 bg-gray-50/80">
                            {SERVICE_OPTIONS.map((opt) => (
                                <label key={opt} className="inline-flex items-center gap-2 cursor-pointer shrink-0">
                                    <input
                                        type="checkbox"
                                        checked={(formData.service || []).includes(opt)}
                                        onChange={() => toggleServiceOption(opt)}
                                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-gray-800">{opt}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Extra service</label>
                            <textarea name="extraService" rows={2} value={formData.extraService} onChange={handleChange} className={inputClass} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Website remarks</label>
                            <textarea name="websiteRemarks" rows={2} value={formData.websiteRemarks} onChange={handleChange} className={inputClass} />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <MediaUploadSection label="WeChat QR" mediaType="wechatQR" urls={formData.wechatQR} onUrlsChange={(urls) => setFormData((p) => ({ ...p, wechatQR: urls }))} memberId={nonMember?._id} apiResource="nonmembers" accept="image/*" multiple />
                        <MediaUploadSection label="Standard Photos" mediaType="standardPhotos" urls={formData.standardPhotos} onUrlsChange={(urls) => setFormData((p) => ({ ...p, standardPhotos: urls }))} memberId={nonMember?._id} apiResource="nonmembers" accept="image/*" multiple />
                        <MediaUploadSection label="Icecream Photos" mediaType="icecreamPhotos" urls={formData.icecreamPhotos} onUrlsChange={(urls) => setFormData((p) => ({ ...p, icecreamPhotos: urls }))} memberId={nonMember?._id} apiResource="nonmembers" accept="image/*" multiple />
                        <MediaUploadSection label="Standard Videos" mediaType="standardVideos" urls={formData.standardVideos} onUrlsChange={(urls) => setFormData((p) => ({ ...p, standardVideos: urls }))} memberId={nonMember?._id} apiResource="nonmembers" accept="video/*" multiple />
                        <MediaUploadSection label="Icecream Videos" mediaType="icecreamVideos" urls={formData.icecreamVideos} onUrlsChange={(urls) => setFormData((p) => ({ ...p, icecreamVideos: urls }))} memberId={nonMember?._id} apiResource="nonmembers" accept="video/*" multiple />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Remarks</label>
                        <textarea name="remarks" rows={4} value={formData.remarks} onChange={handleChange} className={inputClass} />
                    </div>

                    <div className="flex justify-end space-x-4">
                        <button type="button" onClick={onCancel} className="px-4 py-2 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200">
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className={`px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            {loading ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditNonMemberForm;
